import { useI18n } from "i18n";
import { useHistory } from "react-router-dom";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Link } from "@remo-co/ui-core/src/components/Link";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useContext } from "react";
import { isTrialCompleted } from "modules/companyPlanSettings/utils/trial";
import { BlueDot } from "modules/app/branding/components/BlueDot";
import eventsSVG from "./assets/events.svg";
import remoByEventsSVG from "./assets/remo-by-events.svg";
import facebookSVG from "./assets/facebook.svg";
import xSVG from "./assets/x.svg";
import linkedinSVG from "./assets/linkedin.svg";
import instagramSVG from "./assets/instagram.svg";
import pinterestSVG from "./assets/pinterest.svg";
import youtubeSVG from "./assets/youtube.svg";
import { styles } from "./styles";

interface Props {
  hideCreateEventButton?: boolean;
}

export const Footer = ({
  hideCreateEventButton = false,
}: Props): JSX.Element => {
  const { t } = useI18n(["footer", "url", "common"]);
  const { myCompanyPlan } = useCompanyPlanSettings();
  const trialExpired = isTrialCompleted(myCompanyPlan);
  const history = useHistory();
  const { track } = useContext(TRACKING_CONTEXT);

  const handleCreateEventButtonClick = () => {
    track(Events.NEW_EVENT_BUTTON_CLICKED);
    history.push("/event");
  };

  return (
    <Box sx={styles.container} data-testid="footer-container">
      <Box sx={styles.upperFooterContainer}>
        <Box sx={styles.leftSection}>
          <Box sx={styles.logoContainer}>
            <img src={eventsSVG} alt={t("footer:eventsLogoAlt")} />
            <div style={styles.logoDivider} />
            <img src={remoByEventsSVG} alt={t("footer:remoLogoAlt")} />
          </Box>

          <Box sx={styles.navLinks1}>
            <Link href={t("url:footer.about")} sx={styles.navLink1}>
              {t("footer:about")}
            </Link>
            <Link href={t("url:footer.blog")} sx={styles.navLink1}>
              {t("footer:blog")}
            </Link>
            <Link href={t("url:footer.help")} sx={styles.navLink1}>
              {t("footer:help")}
            </Link>
          </Box>
          <Box sx={styles.navLinks2}>
            <Link href={t("url:footer.company")} sx={styles.navLink2}>
              {t("footer:company")}
            </Link>
            <div style={styles.navLinkDivider} />
            <Link href={t("url:footer.team")} sx={styles.navLink2}>
              {t("footer:team")}
            </Link>
            <div style={styles.navLinkDivider} />
            <Link href={t("url:footer.careers")} sx={styles.navLink2}>
              {t("footer:careers")}
            </Link>
            <div style={styles.navLinkDivider} />
            <Link href={t("url:footer.contact")} sx={styles.navLink2}>
              {t("footer:contact")}
            </Link>
            <div style={styles.navLinkDivider} />
            <Link href={t("url:footer.press")} sx={styles.navLink2}>
              {t("footer:press")}
            </Link>
          </Box>
          <BlueDot style={styles.blueDot} />
        </Box>
        <Box sx={styles.rightSection}>
          {!hideCreateEventButton && (
            <Button
              data-testid="create-event-button"
              variant="primary"
              color="blue"
              aria-label={t("common:button.create.event")}
              sx={styles.createEventButton}
              disabled={trialExpired}
              onClick={handleCreateEventButtonClick}
            >
              <Typography variant="body1">
                {t("common:button.create.event")}
              </Typography>
            </Button>
          )}
          <Box sx={styles.socialLinks}>
            <Link
              href={t("url:footer.facebook")}
              aria-label={t("footer:facebook")}
              sx={styles.socialButton}
            >
              <img src={facebookSVG} alt={t("footer:facebook")} />
            </Link>
            <Link
              href={t("url:footer.x")}
              aria-label={t("footer:x")}
              sx={styles.socialButton}
            >
              <img src={xSVG} alt={t("footer:x")} />
            </Link>
            <Link
              href={t("url:footer.linkedin")}
              aria-label={t("footer:linkedin")}
              sx={styles.socialButton}
            >
              <img src={linkedinSVG} alt={t("footer:linkedin")} />
            </Link>
            <Link
              href={t("url:footer.instagram")}
              aria-label={t("footer:instagram")}
              sx={styles.socialButton}
            >
              <img src={instagramSVG} alt={t("footer:instagram")} />
            </Link>
            <Link
              href={t("url:footer.pinterest")}
              aria-label={t("footer:pinterest")}
              sx={styles.socialButton}
            >
              <img src={pinterestSVG} alt={t("footer:pinterest")} />
            </Link>
            <Link
              href={t("url:footer.youtube")}
              aria-label={t("footer:youtube")}
              sx={styles.socialButton}
            >
              <img src={youtubeSVG} alt={t("footer:youtube")} />
            </Link>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.bottomSection}>
        <Box sx={styles.termsLinks}>
          <Link href={t("url:footer.user.terms")} sx={styles.termsLink}>
            {t("footer:userTerms")}
          </Link>
          <Link href={t("url:footer.customer.terms")} sx={styles.termsLink}>
            {t("footer:customerTerms")}
          </Link>
          <Link href={t("url:footer.privacy")} sx={styles.termsLink}>
            {t("footer:privacy")}
          </Link>
        </Box>
        <Typography variant="body2" sx={styles.copyright}>
          {t("footer:copyright")}
        </Typography>
      </Box>
    </Box>
  );
};
